import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Grid, Paper } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { SecondaryTitle } from 'src/shared/components/atoms/secondaryTitle';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import DataGridWrapper from 'src/shared/components/organisms/dataGridWrapper';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { AuthorityEnum } from 'src/shared/models/enums/authority.enum';
import { HelpPage } from '../models/helpPage';
import { HelpPageCategory } from '../models/helpPageCategory';
import { HelpPageModule } from '../models/helpPageModule';
import { HelpPageStatusEnum } from '../models/helpPageStatus.enum';
import { getAllHelpPageCategories } from '../services/helpPageCategoryService';
import { getAllHelpPageModules } from '../services/helpPageModuleService';
import {
  archiveHelpPage,
  createHelpPage,
  getHelpPageById,
  getHelpPageVersions,
  publishHelpPageVersion,
  updateHelpPage,
} from '../services/helpPageService';
import { HelpArticleForm, HelpArticleFormValues } from '../components/helpArticleForm';
import { normalizeHelpPageTags } from '../functions/helpPageTerms';
import { HelpPageVersionPanel } from '../components/helpPageVersionPanel';

const emptyFormValues: HelpArticleFormValues = {
  title: '',
  slug: '',
  categoryId: 0,
  // Gäst är den lägsta riktiga nivån, så en ny artikel syns för alla användare om redaktören inte väljer annat.
  requiredAuthority: AuthorityEnum.Guest,
  modules: [],
  bodyHtml: '',
  changeSummary: '',
};

export const HelpArticleEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['helpArticles']);
  const isMounted = useIsMounted();

  // Skickas med vid navigering från en kategoris "Skapa artikel"-knapp, så kategorin är förvald från start.
  const preSelectedCategoryId = (location.state as { categoryId?: number } | null)?.categoryId;

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [categories, setCategories] = useState<HelpPageCategory[]>([]);
  const [helpPage, setHelpPage] = useState<HelpPage | null>(null);
  // Formuläret monteras först när defaultvärdena är klara, så alla fält (även modul-taggarna) får rätt startvärde.
  const [defaultValues, setDefaultValues] = useState<HelpArticleFormValues | null>(null);
  const [versionPanelKey, setVersionPanelKey] = useState<number>(0);
  // Räknas upp vid varje laddning. Formuläret läser sina startvärden bara när det monteras, så utan den skulle det
  // visa innehållet från före en sparning eller återställning.
  const [formKey, setFormKey] = useState<number>(0);
  const [openArchiveDialog, setOpenArchiveDialog] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const categoryResult = await getAllHelpPageCategories();

      if (!id) {
        if (isMounted()) {
          // Skapa och redigera är samma komponent, så en sida från en tidigare redigering kan ligga kvar. Utan den här
          // raden skulle Spara skriva en ny version på den sidan istället för att skapa en ny.
          setHelpPage(null);
          setCategories(categoryResult.data);
          setDefaultValues({ ...emptyFormValues, categoryId: preSelectedCategoryId ?? emptyFormValues.categoryId });
        }
        return;
      }

      // Moduler behövs bara vid redigering, för att slå upp vilka som redan är valda —
      // HelpArticleForm hämtar dem själv till taggväljaren oavsett. De laddas tillsammans med sidan: utan dem
      // skulle formuläret sakna sidans taggar, och nästa sparning skulle ta bort dem.
      // Versionerna visar inget eget fel: en okänd sida ger 404 på båda anropen, och sidans fel räcker.
      const [moduleResult, pageResult, versionResult] = await Promise.all([
        getAllHelpPageModules(),
        getHelpPageById(id),
        getHelpPageVersions(id, { skipErrorToast: true }),
      ]);
      const allModules: HelpPageModule[] = moduleResult.data;
      // Senaste versionen (inte nödvändigtvis den publicerade) är den man fortsätter redigera på.
      const latestVersion = versionResult.data[0];

      if (isMounted()) {
        setCategories(categoryResult.data);
        setHelpPage(pageResult.data);
        setDefaultValues({
          title: latestVersion?.title ?? '',
          slug: pageResult.data.slug,
          categoryId: pageResult.data.categoryId,
          requiredAuthority: pageResult.data.requiredAuthority,
          modules: allModules.filter((m) => pageResult.data.moduleIds.includes(m.id)),
          bodyHtml: normalizeHelpPageTags(latestVersion?.bodyHtml ?? ''),
          changeSummary: '',
        });
        setFormKey((prev) => prev + 1);
      }
    } catch (e) {
      // Felet visas av interceptorn (utom vid nätverksfel och 401). Tillbaka till listan, så att formuläret aldrig
      // står tomt.
      ConsoleHelper.log(e);
      if (isMounted()) navigate(RoutesEnum.HelpArticles, { replace: true });
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [id, isMounted, navigate, preSelectedCategoryId]);

  useEffect(() => {
    if (isMounted()) loadData();
  }, [isMounted, loadData]);

  /** Sparar formuläret som en ny version och returnerar id:t på både sidan och versionen. */
  const save = async (values: HelpArticleFormValues): Promise<{ helpPageId: string; versionId: string; versionNumber: number } | null> => {
    const moduleIds = values.modules.map((m) => m.id);

    if (!helpPage) {
      const created = await createHelpPage({
        slug: values.slug,
        categoryId: Number(values.categoryId),
        requiredAuthority: Number(values.requiredAuthority),
        moduleIds,
        title: values.title,
        bodyHtml: normalizeHelpPageTags(values.bodyHtml),
        changeSummary: values.changeSummary,
      });
      return { helpPageId: created.data.id, versionId: created.data.currentVersionId, versionNumber: 1 };
    }

    const newVersion = await updateHelpPage(helpPage.id, {
      categoryId: Number(values.categoryId),
      requiredAuthority: Number(values.requiredAuthority),
      moduleIds,
      title: values.title,
      bodyHtml: normalizeHelpPageTags(values.bodyHtml),
      changeSummary: values.changeSummary,
    });
    return { helpPageId: helpPage.id, versionId: newVersion.data.id, versionNumber: newVersion.data.versionNumber };
  };

  const afterSave = (helpPageId: string) => {
    if (!id) {
      // Efter att en ny artikel skapats fortsätter man redigera den befintliga i stället för att skapa en till.
      navigate(`${RoutesEnum.HelpArticleEdit}/${helpPageId}`, { replace: true });
    } else {
      setVersionPanelKey((prev) => prev + 1);
      loadData();
    }
  };

  const handleSaveDraft = async (values: HelpArticleFormValues) => {
    setSaving(true);
    try {
      const result = await save(values);
      if (result && isMounted()) {
        toast.success(t('helpArticles:form.savedSuccess', { versionNumber: result.versionNumber }) as string);
        afterSave(result.helpPageId);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const handlePublish = async (values: HelpArticleFormValues) => {
    setSaving(true);
    try {
      const result = await save(values);
      if (!result) return;

      // Utkastet är redan sparat här. Misslyckas publiceringen visas det med egen text, och sidan laddas ändå som
      // efter en vanlig sparning — annars står en ny sida kvar på skapa-formuläret och nästa försök ger en dubblett.
      try {
        await publishHelpPageVersion(result.helpPageId, result.versionId);
        if (isMounted()) toast.success(t('helpArticles:form.publishedSuccess') as string);
      } catch (e: any) {
        ConsoleHelper.log(e);
        // Interceptorn avvisar med svaret; API:ts felmeddelande är en sträng.
        const message = typeof e?.data === 'string' ? e.data : '';
        if (isMounted()) toast.error(t('helpArticles:form.publishFailed', { versionNumber: result.versionNumber, message }) as string);
      }

      if (isMounted()) afterSave(result.helpPageId);
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!helpPage || saving) return;
    setSaving(true);
    try {
      await archiveHelpPage(helpPage.id);
      if (isMounted()) {
        toast.success(t('helpArticles:form.archivedSuccess') as string);
        navigate(RoutesEnum.HelpArticles);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setSaving(false);
    }
  };

  const isArchived = helpPage?.status === HelpPageStatusEnum.Archived;

  return (
    <GridWrapper>
      <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <MainContentHeader title={id ? t('helpArticles:editArticle') : t('helpArticles:newArticle')} />
        <Button startIcon={<ArrowBack fontSize='small' />} onClick={() => navigate(RoutesEnum.HelpArticles)}>
          {t('helpArticles:form.backToList')}
        </Button>
      </Grid>

      {/* API:t nekar ändringar av en arkiverad artikel, så den visas bara för läsning. */}
      {isArchived && (
        <Grid item xs={12}>
          <Alert severity='info'>{t('helpArticles:form.archivedInfo')}</Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <Paper>
          <Box className='section-inset' sx={{ pt: 3 }}>
            <DataGridWrapper loading={loading}>
              {defaultValues ? (
                <HelpArticleForm
                  key={`${helpPage?.id ?? 'new'}-${formKey}`}
                  defaultValues={defaultValues}
                  categories={categories}
                  isEdit={!!helpPage}
                  saving={saving}
                  readOnly={isArchived}
                  onSaveDraft={handleSaveDraft}
                  onPublish={handlePublish}
                  onArchive={helpPage ? () => setOpenArchiveDialog(true) : undefined}
                />
              ) : (
                <Box />
              )}
            </DataGridWrapper>
          </Box>
        </Paper>
      </Grid>

      {helpPage && (
        <Grid item xs={12} sx={{ mt: 5 }}>
          <Paper>
            <SecondaryTitle title={t('helpArticles:versions.title')} divider uppercase />
            <HelpPageVersionPanel key={versionPanelKey} helpPageId={helpPage.id} readOnly={isArchived} onRestored={() => loadData()} />
          </Paper>
        </Grid>
      )}

      <DeleteConfirmationDialog
        open={openArchiveDialog}
        title={t('helpArticles:form.archiveConfirmTitle')}
        modaltext={t('helpArticles:form.archiveConfirmText')}
        value={helpPage?.id ?? ''}
        confirmButtonText={t('helpArticles:form.archive')}
        confirmButtonColor='error'
        onClose={(value) => {
          // Dialogen stängs direkt, så att OK inte kan ge en andra arkivering medan anropet pågår.
          setOpenArchiveDialog(false);
          if (value) handleArchive();
        }}
      />
    </GridWrapper>
  );
};
